import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../utils/sentryHelpers';

const client = generateClient();

export const useCompanyTopics = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const saveAllCompanyTopics = useCallback(async (companiesData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Save topics and keywords for each company
      const promises = companiesData.map(async (company) => {
        // 1. First update the company with keywords
        const updateCompanyMutation = /* GraphQL */ `
          mutation UpdateCompany($input: UpdateCompanyInput!) {
            updateCompany(input: $input) {
              id
              keywords
              topics {
                items {
                  id
                  topicId
                  topic {
                    id
                  }
                }
              }
              updatedAt
            }
          }
        `;
        
        const updateResult = await client.graphql({
          query: updateCompanyMutation,
          variables: {
            input: {
              id: company.companyId,
              keywords: company.keywords
            }
          }
        });
        
        // 2. Get current topics for this company
        const currentTopicConnections = updateResult.data.updateCompany.topics?.items || [];
        const currentTopicIds = currentTopicConnections.map(item => item.topic.id);
        const newTopicIds = company.topics || [];
        
        // 3. Determine which topics to add and remove
        const topicsToAdd = newTopicIds.filter(id => !currentTopicIds.includes(id));
        const topicsToRemove = currentTopicConnections.filter(
          item => !newTopicIds.includes(item.topic.id)
        );
        
        // 4. Create new topic connections
        const createCompaniesTopicsMutation = /* GraphQL */ `
          mutation CreateCompaniesTopics($input: CreateCompaniesTopicsInput!) {
            createCompaniesTopics(input: $input) {
              id
              topicId
              companyId
            }
          }
        `;
        
        const addPromises = topicsToAdd.map(topicId =>
          client.graphql({
            query: createCompaniesTopicsMutation,
            variables: {
              input: {
                companyId: company.companyId,
                topicId: topicId
              }
            }
          })
        );
        
        // 5. Delete removed topic connections
        const deleteCompaniesTopicsMutation = /* GraphQL */ `
          mutation DeleteCompaniesTopics($input: DeleteCompaniesTopicsInput!) {
            deleteCompaniesTopics(input: $input) {
              id
            }
          }
        `;
        
        const deletePromises = topicsToRemove.map(connection =>
          client.graphql({
            query: deleteCompaniesTopicsMutation,
            variables: {
              input: {
                id: connection.id
              }
            }
          })
        );
        
        // Execute all topic operations
        await Promise.all([...addPromises, ...deletePromises]);
        
        return {
          companyId: company.companyId,
          topics: newTopicIds,
          keywords: company.keywords,
          updatedAt: updateResult.data.updateCompany.updatedAt
        };
      });
      
      const results = await Promise.all(promises);
      
      return {
        success: true,
        data: results
      };
      
    } catch (err) {
      console.error('Error saving company topics:', err);
      captureError(err, {
        feature: 'admin-onboarding',
        operation: 'save-all-company-topics',
        consoleMessage: 'Error saving company topics:',
        context: {
          companiesCount: companiesData.length
        }
      });
      setError(err.message || 'Failed to save company topics and keywords');
      return {
        success: false,
        error: err.message || 'Failed to save company topics and keywords'
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getCompanyTopics = useCallback(async (companyId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const getCompanyQuery = /* GraphQL */ `
        query GetCompany($id: ID!) {
          getCompany(id: $id) {
            id
            keywords
            topics {
              items {
                topic {
                  id
                  name {
                    language
                    text
                  }
                  topicGroupTopicsId
                }
              }
            }
          }
        }
      `;
      
      const result = await client.graphql({
        query: getCompanyQuery,
        variables: { id: companyId }
      });
      
      const company = result.data.getCompany;
      const topicIds = company.topics?.items?.map(item => item.topic.id) || [];
      
      return {
        success: true,
        data: {
          topics: topicIds,
          keywords: company.keywords || [],
          hasExistingData: topicIds.length > 0 || (company.keywords && company.keywords.length > 0)
        }
      };
      
    } catch (err) {
      console.error('Error loading company topics:', err);
      captureError(err, {
        feature: 'admin-onboarding',
        operation: 'get-company-topics',
        consoleMessage: 'Error loading company topics:',
        context: {
          companyId
        }
      });
      setError(err.message || 'Failed to load company topics');
      return {
        success: false,
        error: err.message || 'Failed to load company topics'
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    saveAllCompanyTopics,
    getCompanyTopics,
    isLoading,
    error,
    clearError: () => setError(null)
  };
};